// Single source of truth for a player's displayed status, so the screen-
// inventory list and the player detail page never diverge (they used to:
// the list rendered the resolved server `status` running/idle/down while the
// detail hero rendered a separate pairing badge online/offline, ignoring
// `status` — same player, two different pills).
import type { PlayerSummary } from '@displaycms/contracts';

export const STALE_MS = 90_000; // matches the server player:stale-detector job.

export type DerivedState =
  | 'online'
  | 'offline'
  | 'stale'
  | 'unpaired'
  | 'revoked';

/** Last-seen/pairing derivation, used ONLY when the server hasn't supplied a
 *  resolved `status` (rollout safety). */
export function deriveState(player: PlayerSummary, now: number): DerivedState {
  if (player.pairing_state === 'unpaired') return 'unpaired';
  if (player.pairing_state === 'revoked') return 'revoked';
  // pairing_state === 'paired'
  if (player.last_seen_at === null) return 'offline';
  const last = Date.parse(player.last_seen_at);
  if (Number.isNaN(last)) return 'offline';
  const age = now - last;
  if (age < STALE_MS) return 'online';
  if (age < STALE_MS * 4) return 'stale';
  return 'offline';
}

/** The canonical status string both surfaces render: the resolved server
 *  `status` (running/idle/down or a lifecycle badge) when present, else the
 *  last-seen derivation. All i18n labels live under `players:status_badge.*`. */
export function resolveStatus(player: PlayerSummary, now: number): string {
  return player.status ?? deriveState(player, now);
}
